import { useAuth } from '../context/AuthContext';
import { Property } from '../types';
import { supabase } from '../integrations/supabase/client';
import { useEffect, useState } from 'react';

type AppRole = 'admin' | 'diretor' | 'gerente' | 'corretor' | 'rh';

export const usePermissions = () => {
  const { user, profile } = useAuth();
  const [userRole, setUserRole] = useState<AppRole | null>(null);

  // Buscar role da nova tabela user_roles
  useEffect(() => {
    const fetchUserRole = async () => {
      if (!user?.id) return;

      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .single();

      if (!error && data) {
        setUserRole(data.role as AppRole);
      }
    };

    fetchUserRole();
  }, [user?.id]);

  // Verifica se o usuário é RH
  const isRH = () => {
    return userRole === 'rh' || profile?.role === 'rh';
  };

  // Verifica se o usuário é gerente ou diretor
  const isManagerOrDirector = () => {
    return userRole === 'gerente' || userRole === 'diretor' || 
           profile?.role === 'gerente' || profile?.role === 'diretor';
  };

  // Verifica se o usuário é diretor (admin completo)
  const isDirector = () => {
    return userRole === 'diretor' || profile?.role === 'diretor';
  };

  // Verifica se o usuário é corretor
  const isCorretor = () => {
    return userRole === 'corretor' || profile?.role === 'corretor';
  };

  // Verifica se pode acessar o painel RH
  const canAccessRH = () => {
    return isRH() || isDirector();
  };

  // Verifica se pode acessar configurações
  const canAccessSettings = () => {
    return isRH() || isDirector();
  };

  // Verifica se pode acessar gerenciamento de usuários
  const canAccessUsers = () => {
    return isRH() || isDirector();
  };

  // Verifica se o usuário é o captador do imóvel
  const isCapturer = (property: Property) => {
    return user?.id === (property.responsibleBrokerId || property.userId);
  };

  // Verifica se pode ver dados do proprietário
  const canViewOwnerData = (property: Property) => {
    return isManagerOrDirector() || isCapturer(property);
  };

  // Verifica se pode editar o imóvel
  const canEditProperty = (property: Property) => {
    return isManagerOrDirector() || isCapturer(property);
  };

  // Verifica se pode deletar o imóvel - apenas diretor pode deletar
  const canDeleteProperty = () => {
    return isDirector(); // Apenas diretores podem deletar imóveis
  };

  // Filtra propriedades baseado nas permissões do usuário
  const filterPropertiesByPermission = (properties: Property[]) => {
    // Todos os corretores e gerentes veem todas as propriedades
    return properties;
  };

  // Filtra dados sensíveis da propriedade baseado nas permissões
  const filterPropertyData = (property: Property): Property => {
    if (canViewOwnerData(property)) {
      return property; // Retorna todos os dados
    }

    // Remove dados sensíveis para corretores que não são captadores
    return {
      ...property,
      owner: {
        name: '***',
        phone: '***',
        email: '***'
      }
    };
  };

  return {
    isRH,
    isManagerOrDirector,
    isDirector,
    isCorretor,
    canAccessRH,
    canAccessSettings,
    canAccessUsers,
    isCapturer,
    canViewOwnerData,
    canEditProperty,
    canDeleteProperty,
    filterPropertiesByPermission,
    filterPropertyData
  };
};